/**
 * "Already sent another way" (threads/sent-another-way/mockup.html, 2026-10-07).
 *
 * The office delivered the paperwork without our email: a customer portal
 * (FWISD, Chain Store), the mail, by hand, or the customer's own system. This
 * records it: how, when, an optional note and proof file. The server
 * (approve-and-send with `sentAnotherWay`) approves the job if it isn't yet,
 * finalizes the invoice exactly as a send would, writes one row to the send
 * ledger, and releases any held packet. Nobody is emailed.
 *
 * One dialog, opened from: the "No customer email on file" box (Closeout,
 * Resend, Office Review), Closeout's send step, the job page's held-packet
 * line and the Finish Line packet promise.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Paperclip, X } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { usePacketReadiness, type PacketReadiness } from "@/lib/use-packet-readiness";

export type OtherWayChannel = "portal" | "mail" | "in_person" | "other";

const CHANNELS: Array<{ id: OtherWayChannel; label: string; labelField: string; placeholder: string }> = [
    { id: "portal", label: "Portal", labelField: "Which portal", placeholder: "FWISD vendor portal" },
    { id: "mail", label: "Mail", labelField: "Mailed to", placeholder: "Accounts payable, PO Box 1200" },
    { id: "in_person", label: "In person", labelField: "Handed to", placeholder: "Front office, Maria" },
    { id: "other", label: "Other", labelField: "How", placeholder: "The customer's own system" },
];

/** Today in the browser's calendar, YYYY-MM-DD. */
function todayYmd(now: Date = new Date()): string {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** The preset from a taught delivery route (#1738): portal/mail map straight
 *  across; "contact" (hand it to a named person) reads as in person. */
export function presetFromRoute(route: PacketReadiness["delivery_route"] | undefined): {
    channel: OtherWayChannel;
    label: string;
} {
    if (!route) return { channel: "portal", label: "" };
    const channel: OtherWayChannel =
        route.route === "mail" ? "mail" : route.route === "contact" ? "in_person" : "portal";
    return { channel, label: route.label ?? "" };
}

/** "via FWISD vendor portal" / "by mail" / "in person" / "another way". */
export function otherWayHow(channel: string | null | undefined, label: string | null | undefined): string {
    const l = label?.trim() || null;
    switch (channel) {
        case "portal":
            return `via ${l ?? "the customer's portal"}`;
        case "mail":
            return l ? `by mail (${l})` : "by mail";
        case "in_person":
            return l ? `in person (${l})` : "in person";
        default:
            return l ? `via ${l}` : "another way";
    }
}

interface SaveVars {
    channel: OtherWayChannel;
    label: string;
    sentOn: string;
    note: string;
    file: File | null;
}

export function SentAnotherWayDialog({
    jobId,
    open,
    onClose,
    onSaved,
}: {
    jobId: string;
    open: boolean;
    onClose: () => void;
    /** After the server said yes (the host refreshes its own views). */
    onSaved?: () => void;
}) {
    // A fresh form every time it opens.
    const [round, setRound] = useState(0);
    useEffect(() => {
        if (open) setRound((n) => n + 1);
    }, [open]);
    return (
        <Dialog open={open} onClose={onClose} title="Already sent another way">
            {open ? <SentAnotherWayForm key={round} jobId={jobId} onClose={onClose} onSaved={onSaved} /> : null}
        </Dialog>
    );
}

export function SentAnotherWayForm({
    jobId,
    onClose,
    onSaved,
}: {
    jobId: string;
    onClose: () => void;
    onSaved?: () => void;
}) {
    const qc = useQueryClient();
    const readiness = usePacketReadiness(jobId);
    const route = readiness.data?.delivery_route ?? null;
    const preset = presetFromRoute(route);
    const [channel, setChannel] = useState<OtherWayChannel>(preset.channel);
    const [label, setLabel] = useState(preset.label);
    const [touched, setTouched] = useState(false);
    const [sentOn, setSentOn] = useState(todayYmd());
    const [note, setNote] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement | null>(null);

    // The route answers after the form mounts; fill it in unless the office
    // already started typing.
    useEffect(() => {
        if (touched || !route) return;
        const p = presetFromRoute(route);
        setChannel(p.channel);
        setLabel(p.label);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route?.route, route?.label]);

    const save = useMutation({
        mutationFn: async (v: SaveVars) => {
            let proofAttachmentId: string | null = null;
            if (v.file) {
                const fd = new FormData();
                fd.append("file", v.file);
                fd.append("jobId", jobId);
                fd.append("category", "delivery_proof");
                fd.append("notes", "Proof of delivery (sent another way)");
                try {
                    const up = await api.postForm<{ attachment: { id: string } | null }>("/api/attachments/upload", fd);
                    proofAttachmentId = up.attachment?.id ?? null;
                } catch (e) {
                    throw new Error(
                        `The proof didn't upload (${(e as Error).message}). Nothing was saved. Pick a smaller file or remove it and save without proof.`,
                    );
                }
                if (!proofAttachmentId) throw new Error("The proof didn't upload. Nothing was saved.");
            }
            return api.post<{ sent_another_way: { ledgerId: string } | null }>(
                `/api/office-review/jobs/${jobId}/approve-and-send`,
                {
                    sentAnotherWay: {
                        channel: v.channel,
                        label: v.label.trim() || null,
                        sentOn: v.sentOn,
                        note: v.note.trim() || null,
                        proofAttachmentId,
                    },
                },
            );
        },
        onSuccess: (_res, v) => {
            toast.success(`Marked as sent ${otherWayHow(v.channel, v.label)}. Nobody was emailed.`);
            for (const key of [
                ["job", jobId],
                ["job-checkup", jobId],
                ["send-history", jobId],
                ["packet-readiness", jobId],
                ["close-out"],
            ]) {
                void qc.invalidateQueries({ queryKey: key });
            }
            onSaved?.();
            onClose();
        },
        onError: (e: Error) => setError(e.message || "That didn't save. Try again."),
    });

    const def = CHANNELS.find((c) => c.id === channel)!;
    const today = todayYmd();
    const future = sentOn > today;
    const submit = () => {
        if (!sentOn || future || save.isPending) return;
        setError(null);
        save.mutate({ channel, label, sentOn, note, file });
    };

    return (
        <div className="grid gap-4 text-sm" data-testid="sent-another-way-form">
            <p className="m-0 text-muted-foreground">
                Use this when the customer already has the paperwork. It marks the report and invoice as delivered and
                releases a held packet. Nobody gets an email.
            </p>

            <div className="grid gap-1.5">
                <span className="text-[13px] font-semibold">How it went</span>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4" role="radiogroup" aria-label="How it went">
                    {CHANNELS.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            role="radio"
                            aria-checked={channel === c.id}
                            data-testid={`other-way-${c.id}`}
                            onClick={() => {
                                setTouched(true);
                                // Switching away from the rule's channel drops
                                // the rule's label ("by mail (FWISD vendor
                                // portal)" would read wrong); a typed one stays.
                                if (c.id !== channel && route?.label && label === route.label) setLabel("");
                                setChannel(c.id);
                            }}
                            className={cn(
                                "rounded-md border px-2 py-2 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                channel === c.id
                                    ? "border-primary bg-primary/10 text-primary"
                                    : "border-border bg-card text-foreground hover:bg-secondary",
                            )}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid gap-1.5">
                <Label htmlFor="other-way-label" className="text-[13px] font-semibold">
                    {def.labelField} <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                {route?.label && !touched && (
                    <span className="text-xs font-semibold text-primary" data-testid="other-way-preset">
                        Filled in from {route.scope ? `${route.scope}'s` : "this customer's"} rule: “{route.label}”
                    </span>
                )}
                <Input
                    id="other-way-label"
                    value={label}
                    maxLength={200}
                    placeholder={def.placeholder}
                    onChange={(e) => {
                        setTouched(true);
                        setLabel(e.target.value);
                    }}
                />
            </div>

            <div className="grid gap-1.5">
                <Label htmlFor="other-way-date" className="text-[13px] font-semibold">
                    Date sent
                </Label>
                <Input
                    id="other-way-date"
                    type="date"
                    value={sentOn}
                    max={today}
                    onChange={(e) => setSentOn(e.target.value)}
                />
                {future && <span className="text-xs text-destructive">The date sent can&apos;t be in the future.</span>}
            </div>

            <div className="grid gap-1.5">
                <Label htmlFor="other-way-note" className="text-[13px] font-semibold">
                    Note <span className="font-normal text-muted-foreground">(optional)</span>
                </Label>
                <Input
                    id="other-way-note"
                    value={note}
                    maxLength={1000}
                    placeholder="Uploaded in Ariba, confirmation 55120"
                    onChange={(e) => setNote(e.target.value)}
                />
            </div>

            <div className="grid gap-1.5">
                <span className="text-[13px] font-semibold">
                    Proof{" "}
                    <span className="font-normal text-muted-foreground">
                        (optional: a screenshot or PDF of the confirmation)
                    </span>
                </span>
                <input
                    ref={fileRef}
                    id="other-way-proof"
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                    <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-[13px]">
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{file.name}</span>
                        <button
                            type="button"
                            className="rounded p-1 text-muted-foreground hover:text-foreground"
                            aria-label="Remove the proof file"
                            onClick={() => {
                                setFile(null);
                                if (fileRef.current) fileRef.current.value = "";
                            }}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-left text-[13px] text-muted-foreground hover:bg-secondary"
                    >
                        <Paperclip className="h-4 w-4" /> Add a file
                    </button>
                )}
            </div>

            {error && (
                <div
                    role="alert"
                    data-testid="other-way-error"
                    className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive"
                >
                    {error}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3">
                <Button variant="outline" onClick={onClose} disabled={save.isPending}>
                    Cancel
                </Button>
                <Button onClick={submit} disabled={!sentOn || future || save.isPending} data-testid="other-way-save">
                    {save.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                    Mark as sent
                </Button>
            </div>
        </div>
    );
}
